import {
  resultDisplayBucket,
  isPositiveBucket,
  kickResultBucket,
  isKnownKickResult,
  RESULT_BUCKET_CHIP,
} from 'utilities/chartUtils';
import { t } from 'utilities/TranslationUtils';

interface KickResultChipProps {
  result: string;
  /** Pass the session kind so GK/ThrowIn/Corner show "On Target" in green. */
  kind?: string;
  /** Optional numeric onTarget flag used by binary kinds when result strings are non-standard. */
  onTarget?: number | null;
  /** Short label for tight spots ("Away" instead of "Away From Goal"); falls back to the full label when there is no short form. */
  compact?: boolean;
}

const KickResultChip = ({ result, kind, onTarget = null, compact = false }: KickResultChipProps): React.ReactElement => {
  // Display bucket — the same one every kick marker uses, so chip and marker colours always agree.
  const effectiveBucket = resultDisplayBucket({ result, kind: kind ?? null, onTarget }, kind ?? null);
  const chip = RESULT_BUCKET_CHIP[effectiveBucket];

  // Label must share the colour's polarity. Prefer the specific result label, but if it would
  // contradict the colour (e.g. "Off Target" text on a green chip after the flag override), fall back
  // to the bucket's polarity-correct generic label. This makes a mismatched chip impossible.
  // A status outside the registry (e.g. a goalkeeper or corner result string) has no translation of its own, so it
  // also gets the generic polarity label rather than a raw translation key.
  const specificMatchesColour = isKnownKickResult(result)
    && isPositiveBucket(kickResultBucket(result)) === isPositiveBucket(effectiveBucket);
  const fullLabel = specificMatchesColour ? t(`sessions.kickResult_${result}`) : t(chip.genericLabelKey);
  const shortKey = `sessions.kickResultShort_${result}`;
  const shortLabel = compact && specificMatchesColour ? t(shortKey) : fullLabel;
  const label = shortLabel === shortKey ? fullLabel : shortLabel;

  return <span className={`kick-result-chip kick-result-chip--${chip.slug}`}>{label}</span>;
};

export default KickResultChip;
